import { createORPCClient } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { apiFetch } from "@packages/core-client/api/apiFetch.ts";
import { invalidate, queryPath } from "@packages/core-client/query/queryKeys.ts";
import { useEnvironmentVariable } from "@squide/firefly";
import { useMemo } from "react";
import { analyticsContract } from "#shared/analyticsContract.ts";
import { engagementContract } from "#shared/engagementContract.ts";
import { feedbackReportContract } from "#shared/feedbackReportContract.ts";
import { reviewScoresContract } from "#shared/reviewScoresContract.ts";
import { reviewThemesContract } from "#shared/reviewThemesContract.ts";
import { scopeContract } from "#shared/scopeContract.ts";

function createAnalyticsClient(baseUrl: string) {
    const link = new OpenAPILink(analyticsContract, {
        url: `${baseUrl}/api/analytics`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof analyticsContract> = createORPCClient(link);

    return createTanstackQueryUtils(client, { path: queryPath("analytics", ["analytics"]) });
}

export function useAnalyticsClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createAnalyticsClient(apiBaseUrl), [apiBaseUrl]);
}

function createReviewScoresClient(baseUrl: string) {
    const link = new OpenAPILink(reviewScoresContract, {
        url: `${baseUrl}/api/analytics/review-scores`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof reviewScoresContract> = createORPCClient(link);
    const queryUtils = createTanstackQueryUtils(client, { path: queryPath("analytics", ["members", "reviews"]) });

    return { client, queryUtils };
}

export function useReviewScoresClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createReviewScoresClient(apiBaseUrl), [apiBaseUrl]);
}

function createReviewThemesClient(baseUrl: string) {
    const link = new OpenAPILink(reviewThemesContract, {
        url: `${baseUrl}/api/analytics/review-themes`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof reviewThemesContract> = createORPCClient(link);

    return createTanstackQueryUtils(client, { path: queryPath("analytics", ["review-themes"]) });
}

export function useReviewThemesClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createReviewThemesClient(apiBaseUrl), [apiBaseUrl]);
}

function createAnalyticsScopeClient(baseUrl: string) {
    const link = new OpenAPILink(scopeContract, {
        url: `${baseUrl}/api/analytics/scope`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof scopeContract> = createORPCClient(link);

    return createTanstackQueryUtils(client, { path: queryPath("analytics", ["members"]) });
}

export function useAnalyticsScopeClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createAnalyticsScopeClient(apiBaseUrl), [apiBaseUrl]);
}

function createEngagementClient(baseUrl: string) {
    const link = new OpenAPILink(engagementContract, {
        url: `${baseUrl}/api/analytics/engagement`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof engagementContract> = createORPCClient(link);

    return createTanstackQueryUtils(client, { path: queryPath("analytics", ["engagement"]) });
}

export function useEngagementClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createEngagementClient(apiBaseUrl), [apiBaseUrl]);
}

function createFeedbackReportClient(baseUrl: string) {
    const link = new OpenAPILink(feedbackReportContract, {
        url: `${baseUrl}/api/analytics/feedback`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof feedbackReportContract> = createORPCClient(link);

    return createTanstackQueryUtils(client, { path: queryPath("analytics", ["feedback"]) });
}

export function useFeedbackReportClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createFeedbackReportClient(apiBaseUrl), [apiBaseUrl]);
}

export function useSendSurveyReminderMutationOptions() {
    const client = useEngagementClient();

    return client.sendSurveyReminder.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "engagement")
    });
}
